import { ErrorHandler, Injectable } from '@angular/core';

/**
 * Auto-recuperación ante chunks muertos tras un deploy (app#560).
 *
 * Cada deploy regenera los bundles con hash nuevo; una pestaña abierta con el
 * index viejo pide chunks lazy que ya NO existen en el servidor (404) y la
 * navegación se queda rota ("no me carga las identidades") hasta un F5 manual.
 * Este ErrorHandler detecta el fallo de import dinámico y recarga la página
 * UNA vez para coger el index fresco.
 *
 * Guarda anti-bucle: si ya se recargó hace <60 s (sessionStorage, por pestaña)
 * no se vuelve a recargar — si el servidor está roto de verdad, recargar en
 * bucle sería peor que el error.
 */

const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \S+ failed|ChunkLoadError/i;

const RELOAD_FLAG = 'sg_chunk_reload_at';
const RELOAD_COOLDOWN_MS = 60_000;

/** Extrae el mensaje cubriendo Error directo, rejection envuelta por zone.js y strings. */
function errorMessage(error: unknown): string {
  const err = error as { message?: unknown; rejection?: { message?: unknown } } | null;
  return String(err?.rejection?.message ?? err?.message ?? error ?? '');
}

@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
  override handleError(error: unknown): void {
    if (CHUNK_ERROR_RE.test(errorMessage(error)) && this.canReload()) {
      console.warn(
        '[ChunkReload] chunk del bundle anterior no disponible (deploy nuevo) → recargando la página'
      );
      globalThis.location.reload();
      return;
    }
    super.handleError(error);
  }

  private canReload(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? 0);
      if (Date.now() - last < RELOAD_COOLDOWN_MS) {
        return false;
      }
      sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      return true;
    } catch {
      // sessionStorage inaccesible: mejor no recargar (sin guarda = riesgo de bucle).
      return false;
    }
  }
}
